import Image from 'next/image';

import { Box, Card, Heading, List, ListItem, Text, useBreakpointValue } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';

export type ExperienceIndustryLeadersSectionProps = StaticExperienceIndustryLeadersSectionProps;

export function ExperienceIndustryLeadersSection({
  title,
  description,
  companies,
}: ExperienceIndustryLeadersSectionProps) {
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    {
      fallback: 'md',
    }
  );
  return (
    <ContentContainer contentMaxWidth='content-max-width-lg'>
      <Card
        as='section'
        bgGradient='linear(180deg, #20003A 0%, #6100B0 100%)'
        border='none'
        color='white'
        display='flex'
        flexDirection='column'
        overflow='hidden'
        position='relative'
        borderRadius={{
          base: '0px',
          md: '12px',
        }}
        gap={{
          base: 6,
          md: 12,
        }}
        mx={{
          base: '-16px',
          md: '0px',
        }}
        padding={{
          base: '32px 16px',
          md: '56px',
        }}
      >
        <Box inset={0} position='absolute'>
          <Image
            alt=''
            loading='lazy'
            quality={75}
            src='/images/experiments/ab_plp_redesign_2025/purple-stripes-diagonal.png'
            style={{
              pointerEvents: 'none',
              objectFit: 'contain',
              objectPosition: 'right bottom',
            }}
            fill
          />
        </Box>
        <Box position='relative' textAlign='center'>
          <Heading
            as='h2'
            fontWeight='600'
            mb={4}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.2',
            }}
          >
            {title}
          </Heading>
          <Text>{description}</Text>
        </Box>
        <List
          alignItems='center'
          borderTop='1px solid #6100B0'
          display='flex'
          flexWrap='wrap'
          gap={8}
          position='relative'
          justifyContent={{
            base: 'center',
            md: 'space-around',
          }}
          pt={{
            base: 6,
            md: 12,
          }}
        >
          {companies.map((company) =>
            company.logo ? (
              <ListItem key={company.name}>
                <SanityImage
                  alt={company.name}
                  image={company.logo}
                  style={{
                    height: isMobile ? '20px' : '30px',
                    width: '100%',
                  }}
                />
              </ListItem>
            ) : null
          )}
        </List>
      </Card>
    </ContentContainer>
  );
}

export async function transformStaticExperienceIndustryLeadersSectionProps() {
  const { getCompanies } = await import('~/features/companies/controllers/get-companies');
  const COMPANIES = ['Google', 'Microsoft', 'Nvidia', 'Intel', 'Dell'];
  const companies = await getCompanies(COMPANIES).then((companies) =>
    companies
      .map((company) => ({
        name: company.name,
        logo: company.lofiLogoLight,
      }))
      .sort((a, b) => COMPANIES.indexOf(a.name) - COMPANIES.indexOf(b.name))
  );

  return {
    title: 'Curriculum developed by the best',
    description: "Learn from instructors who've worked at tech's top companies.",
    companies,
  };
}

export type StaticExperienceIndustryLeadersSectionProps = Awaited<
  ReturnType<typeof transformStaticExperienceIndustryLeadersSectionProps>
>;
